import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Package,
  Tag,
  Loader2,
  Weight,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { 
  ItemGroup, 
  formatGroupHeaderText, 
  getGroupCompletionStatus,
  shouldGroupBeExpandedByDefault
} from '../../utils/procurementGroupingUtils';
import { ProcurementBatchItem } from '../../types';
import { formatCurrency } from '../../lib/utils';

interface GroupedProcurementItemsProps {
  groups: ItemGroup[];
  batch: {
    checkedItems: string[];
    actualQuantities: { [key: string]: number };
  };
  checkingItems: Set<string>;
  updatingQuantity: Set<string>;
  onToggleItem: (productId: string) => Promise<void>;
  onUpdateQuantity: (productId: string, quantity: number) => Promise<void>;
  formatWeight: (item: ProcurementBatchItem) => string | null;
}

interface GroupHeaderProps {
  group: ItemGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onCheckAll: () => void;
  onUncheckAll: () => void;
  canCheckAll: boolean;
  canUncheckAll: boolean;
}

function GroupHeader({ 
  group, 
  isExpanded, 
  onToggle, 
  onCheckAll, 
  onUncheckAll,
  canCheckAll,
  canUncheckAll 
}: GroupHeaderProps) {
  const completionStatus = getGroupCompletionStatus(group);
  
  const getCompletionColor = () => {
    switch (completionStatus) {
      case 'completed':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'partial':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCompletionIcon = () => {
    switch (completionStatus) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Circle className="w-4 h-4" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`border rounded-lg p-4 mb-2 cursor-pointer hover:shadow-sm transition-all ${getCompletionColor()}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0" onClick={onToggle}>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.div>
          
          <div className="flex items-center gap-2 min-w-0">
            {getCompletionIcon()}
            <h3 className="font-semibold text-lg truncate">{group.name}</h3>
          </div>
          
          <div className="hidden sm:flex items-center gap-4">
            <span className="text-sm font-medium whitespace-nowrap">
              {formatGroupHeaderText(group)}
            </span>
            
            {group.collectionPercentage > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${group.collectionPercentage}%` }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className={`h-full ${
                      group.collectionPercentage === 100 
                        ? 'bg-emerald-500' 
                        : 'bg-amber-500'
                    }`}
                  />
                </div>
                <span className="text-xs font-medium">
                  {group.collectionPercentage}%
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Mobile stats and progress */}
        <div className="sm:hidden flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            {formatGroupHeaderText(group)}
          </span>
          {group.collectionPercentage > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${group.collectionPercentage}%` }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className={`h-full ${
                    group.collectionPercentage === 100 
                      ? 'bg-emerald-500' 
                      : 'bg-amber-500'
                  }`}
                />
              </div>
              <span className="text-xs font-medium">
                {group.collectionPercentage}%
              </span>
            </div>
          )}
        </div>
        
        {/* Group Actions */}
        <div className="flex items-center gap-2 sm:flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {canUncheckAll && (
            <Button
              variant="outline"
              size="sm"
              onClick={onUncheckAll}
              className="h-7 px-2 text-xs flex-1 sm:flex-initial"
            >
              Uncheck All
            </Button>
          )}
          {canCheckAll && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCheckAll}
              className="h-7 px-2 text-xs flex-1 sm:flex-initial"
            >
              Check All
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

interface ProcurementItemRowProps {
  item: ProcurementBatchItem;
  isChecked: boolean;
  isChecking: boolean;
  isUpdatingQuantity: boolean;
  isPerformingLinkedOperation: boolean;
  actualQuantity: number;
  weight: string | null;
  index: number;
  onToggleItem: (productId: string) => Promise<void>;
  onUpdateQuantity: (productId: string, quantity: number) => Promise<void>;
}

function ProcurementItemRow({
  item,
  isChecked,
  isChecking,
  isUpdatingQuantity,
  isPerformingLinkedOperation,
  actualQuantity,
  weight,
  index,
  onToggleItem,
  onUpdateQuantity
}: ProcurementItemRowProps) {
  const [quantityValue, setQuantityValue] = useState(actualQuantity.toString());

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityValue(e.target.value);
  };

  const handleQuantityBlur = async () => {
    const newQuantity = parseInt(quantityValue) || 0;
    if (newQuantity !== actualQuantity) {
      await onUpdateQuantity(item.productId, newQuantity);
    }
  };

  const handleQuantityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleQuantityBlur();
    }
  };

  React.useEffect(() => {
    setQuantityValue(actualQuantity.toString());
  }, [actualQuantity]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-all ${
        isChecked ? 'bg-emerald-50 border-emerald-200' : ''
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Top row - Product info */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Checkbox */}
          <div className="flex-shrink-0">
            {isPerformingLinkedOperation ? (
              <div className="w-5 h-5 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
              </div>
            ) : (
              <Checkbox
                checked={isChecked}
                onCheckedChange={() => onToggleItem(item.productId)}
                className="w-5 h-5"
              />
            )}
          </div>

          {/* Product Image */}
          <div className="flex-shrink-0">
            <img
              src={item.image}
              alt={item.productName}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover border border-gray-200"
            />
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate text-sm sm:text-base">
              {item.productName}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {item.brand && (
                <Badge variant="outline" className="text-xs">
                  <Package className="w-3 h-3 mr-1" />
                  {item.brand}
                </Badge>
              )}
              {item.category && (
                <Badge variant="outline" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {item.category}
                </Badge>
              )}
              {weight && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Weight className="w-3 h-3" />
                  {weight}
                </span>
              )}
            </div>
          </div>

          {/* Status Indicator - Hidden on mobile, shown in second row */}
          <div className="hidden sm:block flex-shrink-0">
            {actualQuantity >= item.requiredQuantity ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : actualQuantity > 0 ? (
              <AlertCircle className="w-5 h-5 text-amber-600" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Bottom row - Quantities and mobile status */}
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
          {/* Mobile Status Indicator */}
          <div className="sm:hidden flex-shrink-0">
            {actualQuantity >= item.requiredQuantity ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : actualQuantity > 0 ? (
              <AlertCircle className="w-5 h-5 text-amber-600" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400" />
            )}
          </div>

          {/* Quantities Grid */}
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-3 sm:gap-4">
            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Required</div>
              <div className="font-medium text-gray-900 text-sm">
                {item.requiredQuantity}
              </div>
            </div>

            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Collected</div>
              <Input
                type="number"
                value={quantityValue}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                onKeyDown={handleQuantityKeyDown}
                className="w-16 sm:w-20 text-center h-8 text-sm"
                min="0"
                disabled={isPerformingLinkedOperation}
              />
            </div>

            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Unit Cost</div>
              <div className="font-medium text-gray-900 text-sm">
                {formatCurrency(item.unitCost)}
              </div>
            </div>

            <div className="text-center">
              <div className="text-xs text-gray-500 mb-1">Line Total</div>
              <div className="font-semibold text-gray-900 text-sm">
                {formatCurrency(item.lineTotal)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function GroupedProcurementItems({
  groups,
  batch,
  checkingItems,
  updatingQuantity,
  onToggleItem,
  onUpdateQuantity,
  formatWeight
}: GroupedProcurementItemsProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(
      groups
        .filter(shouldGroupBeExpandedByDefault)
        .map(group => group.name)
    )
  );

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleCheckAllInGroup = async (group: ItemGroup) => {
    const promises = group.items
      .filter(item => !batch.checkedItems.includes(item.productId))
      .map(item => onToggleItem(item.productId));
    
    await Promise.all(promises);
  };

  const handleUncheckAllInGroup = async (group: ItemGroup) => {
    const promises = group.items
      .filter(item => batch.checkedItems.includes(item.productId))
      .map(item => onToggleItem(item.productId));
    
    await Promise.all(promises);
  };

  if (groups.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Items Found
        </h3>
        <p className="text-gray-600">
          No items match your current filter criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.name);
        const canCheckAll = group.items.some(item => !batch.checkedItems.includes(item.productId));
        const canUncheckAll = group.items.some(item => batch.checkedItems.includes(item.productId));

        return (
          <div key={group.name} className="space-y-2">
            <GroupHeader
              group={group}
              isExpanded={isExpanded}
              onToggle={() => toggleGroup(group.name)}
              onCheckAll={() => handleCheckAllInGroup(group)}
              onUncheckAll={() => handleUncheckAllInGroup(group)}
              canCheckAll={canCheckAll}
              canUncheckAll={canUncheckAll}
            />

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-2 overflow-hidden"
                >
                  {group.items.map((item, index) => {
                    const isChecked = batch.checkedItems.includes(item.productId);
                    const isChecking = checkingItems.has(item.productId);
                    const isUpdatingQty = updatingQuantity.has(item.productId);
                    const isPerformingLinkedOperation = isChecking || isUpdatingQty;
                    const actualQuantity = batch.actualQuantities[item.productId] || 0;
                    const weight = formatWeight(item);

                    return (
                      <ProcurementItemRow
                        key={item.productId}
                        item={item}
                        isChecked={isChecked}
                        isChecking={isChecking}
                        isUpdatingQuantity={isUpdatingQty}
                        isPerformingLinkedOperation={isPerformingLinkedOperation}
                        actualQuantity={actualQuantity}
                        weight={weight}
                        index={index}
                        onToggleItem={onToggleItem}
                        onUpdateQuantity={onUpdateQuantity}
                      />
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}